// Copyright 2016 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { DataOrder, Grid, GridData } from "controls/plot/grid-plot/GridData";
import { StreamHologram, StreamHologramProperties } from "datatypes/SoramaLocalApi";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { IColorMap } from "services/ColorMap/IColorMap";
import { IColorMapService } from "services/ColorMap/IColorMapService";
import { FrequencySelectionService } from "services/FrequencySelection/FrequencySelectionService";
import { IMeasurementPreviewService } from "services/Measurement/IMeasurementPreviewService";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { Generic } from "units/Generic";
import { Pressure } from "units/Pressure";
import { Unit } from "units/Unit";

/**
 * MODULE OPERATION DESCRIPTION - NearFieldModule
 * The near field module is a display module that continuously displays holograms.
 * The stream is active when the 'isStreamMeasurementActive' property on the SoundStreamPreviewService are true.
 * Frequency selection for the holograms are provided by the workflowproperty named 'selectedFrequencies'
 */
@transient()
@autoinject
export class NearFieldModule implements IWorkflowModuleLifetime {
    private static hologramColorMapSettingKey = "NearFieldModuleHologramColorMap";

    private streamPromise: Promise<void>;
    private observerSubscriptions: Disposable[] = [];

    private bindPromise: Promise<void>;
    private viewActive: boolean;
    private _hologramColorMap: IColorMap;

    public hologram: GridData;
    public hologramDisplayUnit: Unit = Pressure.pascal;
    moduleInstance: IWorkflowModuleInstance;
    private streamerNearFieldSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<NearFieldModule>, void>;

    @computedFrom("hologram")
    public get aspectRatio(): number {
        return this.hologram ? this.hologram.grid.dimension.x / this.hologram.grid.dimension.y : 16 / 9;
    }

    @computedFrom("_hologramColorMap")
    public get hologramColorMap(): IColorMap {
        return this._hologramColorMap;
    }
    public set hologramColorMap(value: IColorMap) {
        if (!this._hologramColorMap) {
            // Wait until the colorMap service has defined this property, otherwise the saved value is overwritten
            return;
        }

        this._hologramColorMap = value;
        this.colorMapService.setDefaultColorMap(NearFieldModule.hologramColorMapSettingKey, value);
    }

    constructor(
        private readonly previewService: IMeasurementPreviewService,
        private readonly colorMapService: IColorMapService,
        private readonly bindingEngine: BindingEngine,
        private readonly frequencySelectionService: FrequencySelectionService
    ) {}

    //#region Lifecycle functions

    public bind(): void {}

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;

        const observer = this.bindingEngine.propertyObserver(this.previewService, "isHologramAvailable");
        this.observerSubscriptions.push(observer.subscribe(this.onHologramStatusChanged));

        this.viewActive = true;
        this.onHologramStatusChanged();

        const promises = [
            this.colorMapService.getDefaultColorMap(NearFieldModule.hologramColorMapSettingKey).then((colorMap) => {
                this._hologramColorMap = colorMap;
            }),
        ];

        this.bindPromise = Promise.all(promises)
            .catch(() => {})
            .then(() => {});
    }

    public deactivateModule(): void {
        this.bindPromise.then(() => {
            this.viewActive = false;
            while (this.observerSubscriptions.length > 0) this.observerSubscriptions.pop().dispose();
        });
    }

    //#endregion

    //#region Stream functions

    /**
     * This method updates all state related to the status of the video or sound measurements
     */
    private onHologramStatusChanged = () => {
        // Check current status of measurements
        if (this.previewService.isHologramAvailable.available && !this.streamPromise) {
            // Start streaming
            this.streamPromise = this.beginGetHologram();
        } else if (this.previewService.isHologramAvailable.reason) {
            this.moduleInstance
                .createNotification()
                .withUrgency(
                    this.previewService.isHologramAvailable.inProgress
                        ? NotificationUrgency.Info
                        : NotificationUrgency.Warning
                )
                .withMessage(this.previewService.isHologramAvailable.reason)
                .show();
        }
    };

    private beginGetHologram(): Promise<void> {
        const properties = <StreamHologramProperties>{
            frequencies: this.frequencySelectionService.selectedRanges,
        };

        return this.previewService
            .previewHologram(properties)
            .then((hologram: StreamHologram) => this.endGetHologram(hologram))
            .catch((e) => {
                return this.exitGetSpectrum();
            });
    }

    private endGetHologram(hologram: StreamHologram): Promise<void> | void {
        // Define the hologram grid
        const grid = new Grid(
            { x: hologram.width, y: hologram.height },
            { x: 1, y: 1 },
            { x: -0.5, y: -0.5 },
            Generic.unitless
        );

        // Create a grid data object
        this.hologram = new GridData(hologram.data, Pressure.pascal, DataOrder.LeftToRightThenTopToBottom, grid);

        // Request new hologram if needed
        return this.exitGetSpectrum();
    }

    private exitGetSpectrum(): Promise<void> | void {
        if (this.previewService.isHologramAvailable.available && this.viewActive) return this.beginGetHologram();
        else {
            this.streamPromise = null;
        }
    }

    public openStreamerNearFieldSettingsModal(): void {
        if (this.streamerNearFieldSettingsModal && this.streamerNearFieldSettingsModal.isOpen) {
            this.streamerNearFieldSettingsModal.cancel();
            this.streamerNearFieldSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<NearFieldModule>, void>(WorkflowModalCatalog.StreamerNearFieldSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.streamerNearFieldSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.streamerNearFieldSettingsModal = x;
            });
    }
    //#endregion
}
